import { createContext, useContext, type ReactNode } from 'react';
import type { useTtsQueue } from '../hooks/useTtsQueue';

/**
 * V4 Voice GM (2026-06-02) — TTS queue context.
 *
 * One queue per ScenarioRunner instance. Consumers:
 *   - useGMDispatcher → enqueue(rationale) so the V3 narration
 *     SEQUENCES with the V4 paragraph autoplay instead of racing.
 *   - ReadAloudControl → cancelAll + enqueue(paragraph text) so the
 *     replay button always wins.
 *   - MicControl → cancelAll on PTT-down / toggle-start (the V4
 *     mic-press-cancels-TTS magic).
 *   - useAutoNarrate → enqueue(paragraph text) on view change.
 *
 * Provided by ScenarioRunner via TtsQueueProvider; standalone-build
 * paths that don't have a runner mounted get null and degrade.
 */

type TtsQueueValue = ReturnType<typeof useTtsQueue>;

const TtsQueueContext = createContext<TtsQueueValue | null>(null);

export interface TtsQueueProviderProps {
  value: TtsQueueValue;
  children: ReactNode;
}

export function TtsQueueProvider({ value, children }: TtsQueueProviderProps) {
  return (
    <TtsQueueContext.Provider value={value}>{children}</TtsQueueContext.Provider>
  );
}

/**
 * Returns null when no provider is mounted (Read Aloud / Mic in
 * isolation, e.g. the picker placeholder). Consumers must branch.
 */
export function useOptionalTtsQueue(): TtsQueueValue | null {
  return useContext(TtsQueueContext);
}
